import {Dialog, IconButton} from '@wandb/components';
import {clsx} from 'clsx';
import React, {useRef, useState} from 'react';

import {Tailwind, TailwindContents} from '../Tailwind';

type Props = {
  children: React.ReactNode;
  controls?: React.ReactNode;
  controlsPlacement?: React.ComponentProps<typeof Dialog.Popover>['placement'];
  actions?: React.ReactNode;
  actionsPosition?: 'top-left' | 'top-right';
  onFullscreen?: () => void;
  className?: string;
  style?: React.CSSProperties;
  onErrorCapture?: React.ReactEventHandler<HTMLDivElement>;
};

const stopPropagation = (event: React.SyntheticEvent) =>
  event.stopPropagation();

export const ImageInteraction = ({
  children,
  controls,
  controlsPlacement = 'bottom',
  actions,
  actionsPosition = 'top-right',
  onFullscreen,
  className,
  style,
  onErrorCapture,
}: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [controlsOpen, setControlsOpen] = useState(false);
  return (
    <TailwindContents>
      <div
        ref={anchorRef}
        className={clsx('group/image-cell relative min-h-0', className)}
        style={style}
        onErrorCapture={onErrorCapture}>
        <div className="h-full w-full">{children}</div>
        <span
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
          className={clsx(
            'absolute top-4 z-10 !flex !h-24 gap-4 !leading-none transition-opacity',
            actionsPosition === 'top-left' ? 'left-4' : 'right-4',
            // Keep actions visible while the pointer is in the popover.
            controlsOpen
              ? 'opacity-100'
              : 'opacity-0 focus-within:opacity-100 group-hover/image-cell:opacity-100'
          )}>
          {actions}
          {controls != null && (
            <IconButton
              icon="settings-parameters"
              size="sm"
              aria-label="Image controls"
              tooltip={{children: 'Show image controls'}}
              onPress={() => setControlsOpen(true)}
            />
          )}
          {onFullscreen != null && (
            <IconButton
              icon="full-screen-mode-expand"
              size="sm"
              aria-label="Open image fullscreen"
              tooltip={{children: 'Open image fullscreen'}}
              onPress={onFullscreen}
            />
          )}
        </span>
        {controls != null && (
          <Dialog.Popover
            isOpen={controlsOpen}
            onOpenChange={setControlsOpen}
            triggerRef={anchorRef}
            placement={controlsPlacement}
            aria-label="Image controls">
            <Tailwind>
              <div
                className="w-[400px] max-w-[90vw] p-16 text-wb-text-primary [overflow-wrap:anywhere] [&_.confidence-slider__container]:flex-wrap"
                onClick={stopPropagation}
                onKeyDown={event => {
                  event.stopPropagation();
                  if (event.key === 'Escape') {
                    setControlsOpen(false);
                  }
                }}>
                {controls}
              </div>
            </Tailwind>
          </Dialog.Popover>
        )}
      </div>
    </TailwindContents>
  );
};
